import type { FrameGiftOffer } from '@src/utils/frameGiftAnalytics';
import { subscribeDialog } from '@src/components/Dialog/Dialog';
import { GifteeFramePreview } from './GifteeFramePreview';
import { GifteeGiftOffer } from './GifteeGiftOffer';
import { CustomAnswers } from './CustomQuestions';
import { ThemeIcon } from '@src/theme/ThemeIcon';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, FullCardInfo } from '@lub0v/santa-utils';
import {
  showDialog,
  Hint,
  Menu,
  Button,
  MessageIcon,
  Note,
  ParticipantFace,
  PersonCard,
  Stack,
  Typography,
} from '@src/components';
import { WishText } from '@src/components/WishText/WishText';
import './WardView.css';

/** The recipient card shared by Santa's real view and the participant's preview. */
export function WardCard({
  info,
  ward,
  sent,
  busy = false,
  preview = false,
  onSent,
  onChat,
  onUndoSent,
}: {
  info: BoxInfo;
  ward: FullCardInfo | null;
  sent: boolean;
  busy?: boolean;
  preview?: boolean;
  onSent?: () => void;
  onChat?: () => void;
  onUndoSent?: () => void;
}) {
  const { t } = useTranslation();
  const dismissalKey = `santa:gift-offer-dismissed:${info.box.key}:${info.cardId}:${ward?.id ?? ''}`;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const dismissSubscription = useRef<(() => void) | null>(null);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronize the selected recipient with browser storage and gift-status requests.
    try { setDismissedKey(localStorage.getItem(dismissalKey) === '1' ? dismissalKey : null); }
    catch { setDismissedKey(null); }
    return () => { dismissSubscription.current?.(); dismissSubscription.current = null; };
  }, [dismissalKey]);
  const rememberDismissal = () => {
    setDismissedKey(dismissalKey);
    try { localStorage.setItem(dismissalKey, '1'); } catch { /* Still update this visit. */ }
  };

  const [personalGift, setPersonalGift] = useState<{sentAt: string | null; openedAt?: string | null; message: string | null; reply: string | null; thankedAt: string | null} | null>(null);
  const [giftLoading, setGiftLoading] = useState(Boolean(info.box.key && !preview));
  const [giftError, setGiftError] = useState(false);
  const [giftOffer, setGiftOffer] = useState<FrameGiftOffer | null>(null);
  const [giftGroup, setGiftGroup] = useState<'G1' | 'G2' | null>(null);
  useEffect(() => {
    if (!info.box.key || !ward?.id || preview) return;
    let active = true;
    const refresh = async () => {
      try {
        const response = await fetch(`/api/box/${encodeURIComponent(info.box.key)}/gift/personal-status?wardId=${ward.id}${new URLSearchParams(window.location.search).has('price') ? '&price=' + encodeURIComponent(new URLSearchParams(window.location.search).get('price')!) : ''}`, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw Error();
        const data = await response.json();
        if (active) { setPersonalGift(data.gift); setGiftOffer(data.offer ?? null); setGiftGroup(data.group === 'G1' || data.group === 'G2' ? data.group : null); setGiftError(false); }
      } catch { if (active) { setGiftError(true); setGiftGroup(null); } }
      finally { if (active) setGiftLoading(false); }
    };
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronize the selected recipient with browser storage and gift-status requests.
    setGiftLoading(true); setPersonalGift(null); setGiftGroup(null);
    void refresh();
    window.addEventListener('focus', refresh);
    return () => { active = false; window.removeEventListener('focus', refresh); };
  }, [info.box.key, ward?.id, preview, info.giftHistory?.paymentId]);
  const [contactsOpen, setContactsOpen] = useState(false);
  const archived = Boolean(info.box.isArchived);
  const needsDelivery = Boolean(info.box.usePost || info.box.isPhoneRequired);
  const contactLines = [
    info.box.usePost ? ward?.postalRecipient || '' : '',
    info.box.usePost ? [ward?.postalAddress, ward?.postalCode].filter(Boolean).join(', ') : '',
    ward?.phone || '',
  ].filter(Boolean);
  return (
    <PersonCard
      edges={2}
      decoration="sparkles"
      className="ward-card"
      testId="game-ward-card"
      avatar={<ParticipantFace card={ward} size="round" />}
      name={ward?.username}
      editAction={
        <div className="ward-card__menu">
          <Menu
            label={t('game.card.manage')}
            testId="game-ward-menu"
            items={[
              {
                id: 'chat',
                label: t('game.ward.write'),
                icon: <ThemeIcon name="message" fallback={<MessageIcon />} />,
                disabled: preview || archived,
                onSelect: () => onChat?.(),
                testId: 'game-ward-chat',
              },
              ...(sent
                ? [
                    {
                      id: 'undo-sent',
                      label: t('game.gift.notSentYet'),
                      disabled: preview || archived || busy,
                      onSelect: () => onUndoSent?.(),
                      testId: 'game-ward-undo-sent',
                    },
                  ]
                : []),
            ]}
          />
        </div>
      }

    >
      {!ward?.customAnswers?.some((a) => a.field.type === 'santa_letter') && (
        <Stack gap={4} className="ward-card__letter" testId="game-ward-letter">
          <Typography as="h3" role="meta/caps">
            {t('game.ward.letterLabel')}
          </Typography>
          {info.box.useWish ? (
            <>
              <Typography as="h2" role="title/section">
                {t('game.ward.letterHello')}
              </Typography>
              <Typography
                as="div"
                role="body/base"
                color="inherit"
                className="ward-card__wish"
                testId="game-ward-wish"
              >
                <WishText>{ward?.wish?.trim() || t('game.card.emptyWish')}</WishText>
              </Typography>
            </>
          ) : (
            <Note tone="info" testId="game-ward-letters-disabled">
              {t('game.card.wishesDisabled')}
            </Note>
          )}
        </Stack>
      )}
      {needsDelivery &&
        !ward?.customAnswers?.some((a) => ['postal_address', 'delivery_address', 'phone'].includes(a.field.type)) && (
          <Stack gap={3} className="ward-card__contacts">
            <Typography as="h3" role="title/card">
              {t('game.ward.contacts')}
            </Typography>
            <p className="card-sheet__body">
              {contactsOpen
                ? contactLines.join('\n') || t('game.card.emptyDelivery')
                : t('game.ward.contactsHidden')}
            </p>
            <Button variant="secondary" onClick={() => setContactsOpen((open) => !open)}>
              {contactsOpen ? t('game.ward.contactsHide') : t('game.ward.contactsShow')}
            </Button>
          </Stack>
        )}
      <CustomAnswers
        answers={
          preview ? ward?.customAnswers?.filter((a) => a.field.visibleToSanta) : ward?.customAnswers
        }
      />
      {!archived && (
        <div className="ward-card__gift-offer">
          {(giftGroup || personalGift) ? <GifteeGiftOffer
            sentGift={personalGift}
            offer={giftOffer}
            dismissed={dismissedKey === dismissalKey}
            name={ward?.username ?? ''}
            onGift={ward ? (flowId) => {
              showDialog({
              title: t('game.ward.framePreviewTitle'),
              children: <GifteeFramePreview ward={ward} flowId={flowId} boxKey={info.box.key === 'giftee-gift-preview' ? undefined : info.box.key} />,
              testId: 'giftee-frame-dialog',
              mobileFullScreen: true,
              });
              dismissSubscription.current?.();
              dismissSubscription.current = subscribeDialog(next => {
                // Nested help dialogs preserve the offer; only leaving the flow counts.
                if (next) return;
                rememberDismissal();
                dismissSubscription.current?.();
                dismissSubscription.current = null;
              });
            } : undefined}
            onSent={!sent ? onSent : undefined}
            busy={busy}
            disabled={preview || giftLoading || giftError}
          /> : (!sent && <Button fullWidth variant="primary" onClick={onSent} loading={busy} disabled={preview} testId="game-ward-sent">{t('game.ward.giftOfferSentAction')}</Button>)}
          {giftError && <Hint tone="error">{t('boxGift.error')}</Hint>}
        </div>
      )}
    </PersonCard>
  );
}
